import { useCallback, useMemo } from 'react'
import { useApolloClient, useMutation, useQuery } from 'apollo-client'
import { GraphQLError, UserError } from 'helpers'
import type { AnalyticsMetadataInput, PaymentMethodScope } from 'typings/graphql'

import paymentMethodCreateBeginQuery, { type PaymentMethodCreateBeginVariables } from './graph/paymentMethodCreateBegin.graphql'
import paymentMethodCreateCommitQuery, { type PaymentMethodCreateCommitVariables } from './graph/paymentMethodCreateCommit.graphql'
import paymentMethodDelete, { type PaymentMethodDeleteVariables } from './graph/paymentMethodDelete.graphql'
import paymentMethodSetPrimary, { type PaymentMethodSetPrimaryVariables } from './graph/paymentMethodSetPrimary.graphql'

import paymentMethodsQuery from './graph/paymentMethods.graphql'


type CreatePaymentMethodInput = {
  tokenId: PaymentMethodCreateCommitVariables['input']['token']['tokenId']
  paymentMethod: PaymentMethodCreateCommitVariables['input']['token']['paymentMethod']
  actionType: PaymentMethodCreateCommitVariables['input']['actionType']
  couponCode?: string
  metadata: AnalyticsMetadataInput
}

// creates a new payment method
export const usePaymentMethodCreate = () => {
  const apolloClient = useApolloClient()

  return useCallback(async ({ tokenId, paymentMethod, couponCode, metadata, actionType }: CreatePaymentMethodInput) => {
    const beginMutation = async (input: PaymentMethodCreateBeginVariables['input']) => {
      const result = await apolloClient.mutate({
        mutation: paymentMethodCreateBeginQuery,
        variables: {
          input,
        },
      })

      if (result.errors) {
        throw new GraphQLError(result.errors)
      }

      const error = result.data.paymentMethodCreateBegin.error

      if (error) {
        throw new UserError(error)
      }

      return result.data.paymentMethodCreateBegin.data
    }

    const commitMutation = async (input: PaymentMethodCreateCommitVariables['input']) => {
      const result = await apolloClient.mutate({
        mutation: paymentMethodCreateCommitQuery,
        variables: {
          input,
        },
        // update payment methods info
        refetchQueries: [ 'PaymentMethods' ],
      })

      if (result.errors) {
        throw new GraphQLError(result.errors)
      }

      const error = result.data.paymentMethodCreateCommit.error

      if (error) {
        throw new UserError(error)
      }

      return result.data.paymentMethodCreateCommit.data
    }

    const { sessionId } = await beginMutation({
      paymentMethod,
      couponCode,
      metadata,
      primary: actionType === 'UPDATE_PRIMARY', // for legacy api
    })

    return await commitMutation({
      actionType,
      sessionId,
      token: {
        tokenId,
        paymentMethod,
      },
    })
  }, [ apolloClient ])
}


type UsePaymentMethodsProps = {
  scope?: PaymentMethodScope
  fetchPolicy?: 'cache-and-network' // only this value is allowed
  skip?: boolean
}

export const usePaymentMethods = (props?: UsePaymentMethodsProps) => {
  const { fetchPolicy, scope, skip } = props || {}

  // So with wallet feature scope doesn't matter anymore, and user will see all payment methods
  //  but without this feature, user can actually update only primary payment method
  const { data, isFetching } = useQuery(paymentMethodsQuery, {
    ssr: false,
    variables: {
      input: {
        scope,
      },
    },
    fetchPolicy,
    skip,
  })

  const paymentMethods = useMemo<UserModule.PaymentMethod[]>(() => {
    const items = data?.currentUser?.data?.paymentMethods

    if (!items) {
      return null
    }

    return items
      .map((item) => ({
        ...item,
        lastDigits: item.type === 'CREDIT_CARD' ? item.name.replace(/.*(\d{4})$/, '$1') : null,
      }))
  }, [ data ])

  return {
    paymentMethods,
    primaryPaymentMethod: paymentMethods?.find(({ primary }) => primary),
    validPaymentMethod: paymentMethods?.find(({ valid }) => valid),
    isFetching,
  }
}

export const usePaymentMethodDelete = () => {
  const [ mutate, { isFetching } ] = useMutation(paymentMethodDelete)

  const submit = useCallback(async (input: PaymentMethodDeleteVariables['input']) => {
    const result = await mutate({
      variables: {
        input,
      },
      // update payment methods info
      refetchQueries: [ 'PaymentMethods' ],
      awaitRefetchQueries: true,
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { error } = result.data.paymentMethodDelete

    if (error) {
      throw new UserError(error)
    }
  }, [ mutate ])

  return [
    submit,
    {
      isFetching,
    },
  ] as const
}

export const usePaymentMethodSetPrimary = () => {
  const [ mutate, { isFetching } ] = useMutation(paymentMethodSetPrimary)

  const submit = useCallback(async (input: PaymentMethodSetPrimaryVariables['input']) => {
    const result = await mutate({
      variables: {
        input,
      },
      // update payment methods info
      refetchQueries: [ 'PaymentMethods' ],
      awaitRefetchQueries: true,
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { error } = result.data.paymentMethodSetPrimary

    if (error) {
      throw new UserError(error)
    }
  }, [ mutate ])

  return [
    submit,
    {
      isFetching,
    },
  ] as const
}
